import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const DEFAULT_TITLE = 'Gildy - Quotazioni Oro e Argento in Tempo Reale';

/**
 * Rete di sicurezza per il titolo pagina.
 *
 * Se dopo un cambio di route nessuna pagina aggiorna il titolo (pagina senza
 * <PageSEO>, oppure titolo emesso solo da SSR come la home), il titolo della
 * pagina precedente resterebbe visibile. Qui, poco dopo la navigazione, se il
 * titolo non è cambiato applichiamo il titolo di riferimento della rotta.
 *
 * Non agisce al primo render (idratazione) per non toccare il titolo SSR.
 */
export function useTitleFallback() {
  const { pathname } = useLocation();
  const isFirstRun = useRef(true);

  useEffect(() => {
    if (typeof document === 'undefined') return;

    if (isFirstRun.current) {
      isFirstRun.current = false;
      // Primo render: non tocchiamo il titolo SSR, solo il caso di titolo assente
      if (!document.title.trim()) {
        document.title = resolveFallbackTitle(pathname);
      }
      return;
    }

    const titleBeforeNavigation = document.title;

    const timer = window.setTimeout(() => {
      if (document.title !== titleBeforeNavigation) return;
      document.title = resolveFallbackTitle(pathname);
    }, 600);

    return () => window.clearTimeout(timer);
  }, [pathname]);
}

function resolveFallbackTitle(pathname: string): string {
  if (pathname === '/' || pathname === '') {
    return resolveHomeTitle();
  }
  return DEFAULT_TITLE;
}

/**
 * Ricostruisce il titolo home usato da SSR (con il numero reale di compro oro
 * quando disponibile nei dati già presenti in pagina).
 */
function resolveHomeTitle(): string {
  try {
    const homeData = (window as any).__HOME_PAGE_DATA__;
    const count =
      homeData?.realDealerStats?.totalDealers ??
      homeData?.dealerStats?.totalCount ??
      homeData?.dealerStats?.totalDealers ??
      0;

    if (typeof count === 'number' && count > 0) {
      return `Gildy - Quotazioni Oro e Argento | ${count} Compro Oro`;
    }
  } catch {
    // fallback silenzioso
  }
  return DEFAULT_TITLE;
}
